import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { HoverInsight } from '../types';
import api from '../lib/api';
import { formatIST, formatChartDate } from '../lib/formatDate';
import { DateRangePicker } from '../components/DateRangePicker';
import { useGlobalDateRange, clampRangeToBounds } from '../context/GlobalDateRangeContext';
import { Tabs as TabsBase, TabsList as TabsListBase, TabsTrigger as TabsTriggerBase, TabsContent as TabsContentBase } from '../components/ui/tabs';
import { ShoppingBag, TrendingUp, TrendingDown } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';

interface ShopifyDTCProps {
  onHover: (insight: HoverInsight | null) => void;
}

interface Summary {
  connected: boolean;
  synced_at: string | null;
  total_revenue: number;
  total_orders: number;
  aov: number;
  fulfilled_count: number;
  fulfilled_pct: number;
  daily_trend: { date: string; revenue: number; orders: number }[];
  financial_status_breakdown: { name: string; count: number }[];
  returning_customer_rate: { pct: number; returning: number; total: number } | null;
}

interface FinancialsData {
  connected: boolean;
  total_sales: number;
  total_sales_change_pct: number | null;
  breakdown: { key: string; value: number; change_pct: number | null }[];
  daily: { date: string; total_sales: number }[];
  daily_previous: { date: string; total_sales: number }[];
}

interface SessionsData {
  connected: boolean;
  sessions: number;
  sessions_change_pct: number | null;
  conversion_rate: number;
  conversion_rate_change_pct: number | null;
  average_order_value: number;
  funnel: { stage: string; value: number; pct_of_total: number; change_pct: number | null }[];
  sessions_daily: { date: string; sessions: number }[];
  sessions_daily_previous: { date: string; sessions: number }[];
  conversion_daily: { date: string; conversion_rate: number }[];
  conversion_daily_previous: { date: string; conversion_rate: number }[];
  aov_daily: { date: string; average_order_value: number }[];
  aov_daily_previous: { date: string; average_order_value: number }[];
  sales_by_channel: { name: string; value: number }[];
  sales_by_product: { title: string; value: number; change_pct: number | null }[];
}

interface AcquisitionData {
  connected: boolean;
  devices: { name: string; value: number }[];
  locations: { name: string; value: number; prev_value: number; change_pct: number | null }[];
  social_referrers: { name: string; value: number }[];
  sales_by_referrer: { name: string; value: number; change_pct: number | null }[];
  performance_by_channel: { name: string; value: number }[];
}

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const compactInr = (n: number) => `₹${Math.abs(n) >= 100000 ? `${(n / 100000).toFixed(2)}L` : (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const toISO = (d: Date) => format(d, 'yyyy-MM-dd');
const BREAKDOWN_LABELS: Record<string, string> = {
  gross_sales: 'Gross sales', discounts: 'Discounts', returns: 'Returns', net_sales: 'Net sales',
  shipping_charges: 'Shipping charges', return_fees: 'Return fees', taxes: 'Taxes', total_sales: 'Total sales',
  orders: 'Orders', average_order_value: 'Average order value',
};
const CHART_COLORS = ['#95BF47', '#0EA5E9', '#F59E0B', '#EC4899', '#8B5CF6', '#14B8A6', '#F43F5E', '#64748B'];
const Tabs: any = TabsBase;
const TabsList: any = TabsListBase;
const TabsTrigger: any = TabsTriggerBase;
const TabsContent: any = TabsContentBase;

// Some Shopify checkout-extension channels report as "<raw id> - AppName" (e.g. a MagicCheckout
// app install) — strip the leading id/hash so the legend shows a human-readable app name.
const cleanChannelName = (name: string) => {
  if (!name) return 'Unknown';
  const dashIdx = name.indexOf(' - ');
  if (dashIdx > -1 && /^[a-zA-Z0-9]+$/.test(name.slice(0, dashIdx).trim())) return name.slice(dashIdx + 3).trim();
  return name;
};

const ChangeBadge = ({ pct }: { pct: number | null | undefined }) => {
  if (pct === null || pct === undefined) return <span className="text-[11px] text-slate-400 font-mono">—</span>;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold font-mono ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
};

export default function ShopifyDTC({ onHover }: ShopifyDTCProps) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('financials');

  const [financials, setFinancials] = useState<FinancialsData | null>(null);
  const [financialsLoading, setFinancialsLoading] = useState(false);
  const [sessionsData, setSessionsData] = useState<SessionsData | null>(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [acquisition, setAcquisition] = useState<AcquisitionData | null>(null);
  const [acquisitionLoading, setAcquisitionLoading] = useState(false);

  const today = useMemo(() => new Date(), []);
  const ninetyDaysAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 90); return d; }, []);
  const { globalRange } = useGlobalDateRange();
  const [range, setRange] = useState<{ from: Date; to: Date }>(() => clampRangeToBounds(globalRange, ninetyDaysAgo, today));

  useEffect(() => {
    const start_date = toISO(range.from);
    const end_date = toISO(range.to);
    setLoading(true);
    // Both calls hit our own MongoDB (already synced from Shopify in the background) — selecting
    // a date does NOT re-hit the Shopify API, it just re-aggregates the already-synced data.
    api.get('/revenue-ops/shopify-orders/summary', { params: { start_date, end_date } })
      .then((res) => setSummary(res.data))
      .finally(() => setLoading(false));

    // ShopifyQL-backed tabs run LIVE against the Shopify Admin API for 100% accuracy — invalidate
    // cached tab data on every range change and refetch whichever tab is currently open.
    setFinancials(null);
    setSessionsData(null);
    setAcquisition(null);
  }, [range]);

  useEffect(() => {
    const start_date = toISO(range.from);
    const end_date = toISO(range.to);
    if (activeTab === 'financials' && !financials && !financialsLoading) {
      setFinancialsLoading(true);
      api.get('/revenue-ops/shopify/financials', { params: { start_date, end_date } })
        .then((res) => setFinancials(res.data)).finally(() => setFinancialsLoading(false));
    }
    if (activeTab === 'sessions' && !sessionsData && !sessionsLoading) {
      setSessionsLoading(true);
      api.get('/revenue-ops/shopify/sessions', { params: { start_date, end_date } })
        .then((res) => setSessionsData(res.data)).finally(() => setSessionsLoading(false));
    }
    if (activeTab === 'acquisition' && !acquisition && !acquisitionLoading) {
      setAcquisitionLoading(true);
      api.get('/revenue-ops/shopify/acquisition', { params: { start_date, end_date } })
        .then((res) => setAcquisition(res.data)).finally(() => setAcquisitionLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, financials, sessionsData, acquisition, range]);

  const dailyTrend = useMemo(() => (summary?.daily_trend || []).map((d) => ({
    date: formatChartDate(d.date),
    revenue: d.revenue,
    orders: d.orders,
  })), [summary]);

  const financialsChart = useMemo(() => {
    if (!financials) return [];
    const prev = financials.daily_previous || [];
    return (financials.daily || []).map((d, i) => ({
      date: formatChartDate(d.date),
      current: d.total_sales,
      previous: prev[i]?.total_sales ?? null,
    }));
  }, [financials]);

  const sessionsChart = useMemo(() => {
    if (!sessionsData) return [];
    const prev = sessionsData.sessions_daily_previous || [];
    return (sessionsData.sessions_daily || []).map((d, i) => ({
      date: formatChartDate(d.date),
      current: d.sessions,
      previous: prev[i]?.sessions ?? null,
    }));
  }, [sessionsData]);

  const conversionChart = useMemo(() => {
    if (!sessionsData) return [];
    const prev = sessionsData.conversion_daily_previous || [];
    return (sessionsData.conversion_daily || []).map((d, i) => ({
      date: formatChartDate(d.date),
      current: +(d.conversion_rate * 100).toFixed(3),
      previous: prev[i]?.conversion_rate != null ? +(prev[i].conversion_rate * 100).toFixed(3) : null,
    }));
  }, [sessionsData]);

  const aovChart = useMemo(() => {
    if (!sessionsData) return [];
    const prev = sessionsData.aov_daily_previous || [];
    return (sessionsData.aov_daily || []).map((d, i) => ({
      date: formatChartDate(d.date),
      current: d.average_order_value,
      previous: prev[i]?.average_order_value ?? null,
    }));
  }, [sessionsData]);

  return (
    <div className="space-y-8 animate-fade-in font-sans -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="shopify-dtc-module-root">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] block mb-1">DTC Storefront Performance</span>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">Shopify Orders</h1>
          <p className="text-xs text-slate-500 mt-1 font-sans">Live orders synced directly from nooe-india.myshopify.com (90-day sync window). Financials/Sessions/Acquisition query Shopify's Analytics API live for 100% accuracy.</p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker value={range} onChange={setRange} minDate={ninetyDaysAgo} maxDate={today} testIdPrefix="shopify-orders" presets />
          <div data-testid="shopify-orders-connection-badge" className={`px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-xs font-semibold border ${summary?.connected ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-100 text-slate-400 border-slate-200'}`}>
            <ShoppingBag className="h-4.5 w-4.5" />
            <span>{summary?.connected ? `Synced ${summary.synced_at ? formatIST(summary.synced_at) : ''}` : 'Not Connected'}</span>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="shopify-dtc-tabs">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="financials" data-testid="shopify-tab-financials" className="text-xs font-semibold rounded-lg">Financials</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="shopify-tab-sessions" className="text-xs font-semibold rounded-lg">Sessions & Conversion</TabsTrigger>
          <TabsTrigger value="acquisition" data-testid="shopify-tab-acquisition" className="text-xs font-semibold rounded-lg">Acquisition</TabsTrigger>
        </TabsList>

        <TabsContent value="financials" className="mt-6 space-y-6" data-testid="shopify-financials-tab">
          {financialsLoading || !financials ? (
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-400 font-mono">Querying Shopify Analytics live…</div>
          ) : !financials.connected ? (
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-500">Shopify isn't connected yet.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="shopify-total-sales-over-time">
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Total sales over time</h3>
                  <ChangeBadge pct={financials.total_sales_change_pct} />
                </div>
                <p className="text-2xl font-bold text-slate-800 font-mono">{inr(financials.total_sales)}</p>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={financialsChart} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} tickLine={false} />
                      <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} tickFormatter={(v) => compactInr(v)} />
                      <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} formatter={(v: number) => inr(v)} />
                      <Line type="monotone" dataKey="current" stroke="#95BF47" strokeWidth={2.5} dot={false} name="Current period" />
                      <Line type="monotone" dataKey="previous" stroke="#95BF47" strokeOpacity={0.35} strokeDasharray="4 4" strokeWidth={2} dot={false} name="Previous period" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-1" data-testid="shopify-total-sales-breakdown">
                <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase mb-3">Total sales breakdown</h3>
                {financials.breakdown.map((row) => (
                  <div key={row.key} className="flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0">
                    <span className={`text-sm ${row.key === 'total_sales' ? 'font-bold text-slate-800' : 'text-slate-600'}`}>{BREAKDOWN_LABELS[row.key] || row.key}</span>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-mono font-semibold ${row.key === 'total_sales' ? 'text-slate-900' : 'text-slate-700'}`}>
                        {row.key === 'orders' ? row.value.toLocaleString('en-IN') : inr(row.value)}
                      </span>
                      <ChangeBadge pct={row.change_pct} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="sessions" className="mt-6 space-y-6" data-testid="shopify-sessions-tab">
          {sessionsLoading || !sessionsData ? (
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-400 font-mono">Querying Shopify Analytics live…</div>
          ) : !sessionsData.connected ? (
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-500">Shopify isn't connected yet.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-3" data-testid="shopify-sales-by-channel">
                <h3 className="font-display font-semibold text-slate-700 text-xs tracking-wide uppercase">Total sales by sales channel</h3>
                <div className="h-52 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={sessionsData.sales_by_channel} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                        {sessionsData.sales_by_channel.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <ChartTooltip formatter={(v: number, _n: any, entry: any) => [inr(v), cleanChannelName(entry?.payload?.name)]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-2">
                  {sessionsData.sales_by_channel.slice(0, 4).map((c, i) => (
                    <span key={c.name} className="flex items-center gap-1.5 text-[10px] text-slate-500"><span className="h-2 w-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />{cleanChannelName(c.name)}</span>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-3" data-testid="shopify-aov-over-time">
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-semibold text-slate-700 text-xs tracking-wide uppercase">Average order value over time</h3>
                </div>
                <p className="text-xl font-bold text-slate-800 font-mono">{inr(sessionsData.average_order_value)}</p>
                <div className="h-40 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={aovChart}>
                      <XAxis dataKey="date" stroke="#94a3b8" fontSize={9} tickLine={false} />
                      <YAxis hide />
                      <ChartTooltip formatter={(v: number) => inr(v)} />
                      <Line type="monotone" dataKey="current" stroke="#0EA5E9" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="previous" stroke="#0EA5E9" strokeOpacity={0.3} strokeDasharray="4 4" strokeWidth={1.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-2" data-testid="shopify-sales-by-product">
                <h3 className="font-display font-semibold text-slate-700 text-xs tracking-wide uppercase mb-1">Total sales by product</h3>
                {sessionsData.sales_by_product.slice(0, 5).map((p) => (
                  <div key={p.title} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-50 last:border-0">
                    <span className="text-slate-600 truncate max-w-[130px]" title={p.title}>{p.title}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-slate-700">{compactInr(p.value)}</span>
                      <ChangeBadge pct={p.change_pct} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-3" data-testid="shopify-sessions-over-time">
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-semibold text-slate-700 text-xs tracking-wide uppercase">Sessions over time</h3>
                  <ChangeBadge pct={sessionsData.sessions_change_pct} />
                </div>
                <p className="text-xl font-bold text-slate-800 font-mono">{sessionsData.sessions.toLocaleString('en-IN')}</p>
                <div className="h-40 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sessionsChart}>
                      <XAxis dataKey="date" stroke="#94a3b8" fontSize={9} tickLine={false} />
                      <YAxis hide />
                      <ChartTooltip />
                      <Line type="monotone" dataKey="current" stroke="#8B5CF6" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="previous" stroke="#8B5CF6" strokeOpacity={0.3} strokeDasharray="4 4" strokeWidth={1.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-3" data-testid="shopify-conversion-rate-over-time">
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-semibold text-slate-700 text-xs tracking-wide uppercase">Conversion rate over time</h3>
                  <ChangeBadge pct={sessionsData.conversion_rate_change_pct} />
                </div>
                <p className="text-xl font-bold text-slate-800 font-mono">{(sessionsData.conversion_rate * 100).toFixed(2)}%</p>
                <div className="h-40 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={conversionChart}>
                      <XAxis dataKey="date" stroke="#94a3b8" fontSize={9} tickLine={false} />
                      <YAxis hide />
                      <ChartTooltip formatter={(v: number) => `${v}%`} />
                      <Line type="monotone" dataKey="current" stroke="#F59E0B" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="previous" stroke="#F59E0B" strokeOpacity={0.3} strokeDasharray="4 4" strokeWidth={1.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-2" data-testid="shopify-conversion-funnel">
                <h3 className="font-display font-semibold text-slate-700 text-xs tracking-wide uppercase mb-2">Conversion rate breakdown</h3>
                {sessionsData.funnel.map((f, i) => (
                  <div key={f.stage} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-600">{f.stage}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-slate-700">{f.value.toLocaleString('en-IN')} · {f.pct_of_total.toFixed(2)}%</span>
                        <ChangeBadge pct={f.change_pct} />
                      </div>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.max(f.pct_of_total, 1)}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="acquisition" className="mt-6 space-y-6" data-testid="shopify-acquisition-tab">
          {acquisitionLoading || !acquisition ? (
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-400 font-mono">Querying Shopify Analytics live…</div>
          ) : !acquisition.connected ? (
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-500">Shopify isn't connected yet.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-3" data-testid="shopify-sessions-by-device">
                <h3 className="font-display font-semibold text-slate-700 text-xs tracking-wide uppercase">Sessions by device type</h3>
                <div className="h-52 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={acquisition.devices} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                        {acquisition.devices.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <ChartTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-2">
                  {acquisition.devices.map((d, i) => (
                    <span key={d.name} className="flex items-center gap-1.5 text-[10px] text-slate-500"><span className="h-2 w-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />{d.name} · {d.value.toLocaleString('en-IN')}</span>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-2 lg:col-span-2" data-testid="shopify-sessions-by-location">
                <h3 className="font-display font-semibold text-slate-700 text-xs tracking-wide uppercase mb-2">Sessions by location</h3>
                {acquisition.locations.map((l) => (
                  <div key={l.name} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-600">{l.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-slate-700">{l.value.toLocaleString('en-IN')}</span>
                        <ChangeBadge pct={l.change_pct} />
                      </div>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-cyan-500" style={{ width: `${Math.min((l.value / (acquisition.locations[0]?.value || 1)) * 100, 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-2" data-testid="shopify-sessions-by-social-referrer">
                <h3 className="font-display font-semibold text-slate-700 text-xs tracking-wide uppercase mb-2">Sessions by social referrer</h3>
                {acquisition.social_referrers.length === 0 ? (
                  <p className="text-xs text-slate-400 py-6 text-center">No social-referred sessions in this window.</p>
                ) : acquisition.social_referrers.map((r) => (
                  <div key={r.name} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-50 last:border-0">
                    <span className="text-slate-600 capitalize">{r.name}</span>
                    <span className="font-mono font-semibold text-slate-700">{r.value.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-2" data-testid="shopify-sales-by-referrer">
                <h3 className="font-display font-semibold text-slate-700 text-xs tracking-wide uppercase mb-2">Total sales by referrer</h3>
                {acquisition.sales_by_referrer.slice(0, 8).map((r) => (
                  <div key={r.name} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-50 last:border-0">
                    <span className="text-slate-600 capitalize truncate max-w-[110px]" title={r.name}>{r.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-slate-700">{compactInr(r.value)}</span>
                      <ChangeBadge pct={r.change_pct} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-3" data-testid="shopify-performance-by-channel">
                <h3 className="font-display font-semibold text-slate-700 text-xs tracking-wide uppercase">Performance by referring channel</h3>
                <div className="h-48 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={acquisition.performance_by_channel} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={11} tickLine={false} width={70} />
                      <ChartTooltip formatter={(v: number) => inr(v)} />
                      <Bar dataKey="value" fill="#95BF47" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
